"use client";

import type { TaskRunEvent } from "@mlx/contracts";
import { formatUpdateCount } from "@product/components/app/runs/run-trace-labels";
import type { ComposerSource } from "@product/components/app/sessions/composer-sources-view";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import { formatRunDuration } from "@product/lib/run-details-format";
import { buildRunSourceUsage } from "@product/lib/run-source-usage";

import { AppSurface } from "@/components/editorial/product-bridge/app-surface";
import { ProductLinkSandbox } from "@/components/editorial/product-bridge/product-link-sandbox";

import { ShotComposer } from "./chrome";
import { CALDER_NOW, CALDER_TURN_TIMES, CALDER_USER } from "./fixture-calder";
import { jsonResult } from "./replay-run";
import { ShotTurn } from "./turn";
import { ShotAnswerFooter, ShotClosedTrace } from "./window";

/** A system an answer drew on: the plugin it is connected through, its name and its mark. */
export type TwinChatSource = { slug: string; label: string; iconUrl: string };

/** How long the card's run took: one read of each system, then the answer. */
const RUN_DURATION_MS = 12_400;

/**
 * The run behind the answer, in the shapes the executor emits: one read of
 * each system. The "Retrieved with" row and the trace pill's count are
 * derived from these by the application's own functions, as every shot's
 * are, never written by hand.
 */
function runEvents(sources: readonly TwinChatSource[]): TaskRunEvent[] {
  return sources.flatMap((source, index) => {
    const call = { toolCallId: `call_${index + 1}`, toolName: "warehouse_query", pluginSlug: source.slug, pluginLabel: source.label };
    return [
      ["tool.started", { ...call, args: {} }],
      ["tool.completed", { ...call, result: jsonResult({ rows: 1 }) }],
    ].map(([kind, payload], step) => ({
      id: `evt_twin_chat_${index * 2 + step + 1}`,
      runId: "run_twin_chat",
      seq: index * 2 + step + 1,
      createdAt: CALDER_NOW - 60_000 + (index * 2 + step) * 1_000,
      kind,
      payload,
    }));
  }) as TaskRunEvent[];
}

/**
 * The Twin's own chat as a card: one finished turn of a session in the
 * application's own components, at their own size, as the hero's product
 * shot is. The question in its bubble, the answer, the sources it was
 * retrieved with, then the trace pill and the session's composer in its
 * compact form, so the whole turn fits a card. The composer takes no
 * pointer or focus; the turn is the reader's to read.
 *
 * The card fills its parent, which sets its size and clips it; the wrapper
 * is the scope the shots' stylesheet applies within (product-shot.css).
 */
export function TwinChatCard({
  question,
  answerMarkdown,
  sources,
}: {
  question: string;
  answerMarkdown: string;
  /** The systems the answer was retrieved with, in the order it names them. */
  sources: readonly TwinChatSource[];
}) {
  const events = runEvents(sources);
  const plugins: PluginIconPlugin[] = sources.map(({ slug, label, iconUrl }) => ({ slug, label, branding: { iconUrl } }));
  const composerSources: ComposerSource[] = sources.map(({ slug, label, iconUrl }) => ({ id: slug, label, pluginSlugs: [slug], iconUrl }));
  return (
    <div data-shot-scope="" className="h-full">
      <AppSurface tone="light" className="flex h-full flex-col overflow-hidden">
        <ProductLinkSandbox>
          <div className="min-h-0 flex-1 overflow-hidden px-3 pt-3">
            <ShotTurn
              question={question}
              answerMarkdown={answerMarkdown}
              sources={buildRunSourceUsage(events)}
              plugins={plugins}
              user={CALDER_USER}
              times={CALDER_TURN_TIMES}
            />
          </div>
          <div inert className="shrink-0 pt-2 pb-3">
            <ShotAnswerFooter
              trace={<ShotClosedTrace updateCountLabel={formatUpdateCount(sources.length)} durationLabel={formatRunDuration(RUN_DURATION_MS)} />}
              showHelpImprove={false}
            />
            <ShotComposer sources={composerSources} compact />
          </div>
        </ProductLinkSandbox>
      </AppSurface>
    </div>
  );
}
